import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBellOff,
  IconCheck,
  IconClock,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { AlertEvent, AlertRule } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDateTime } from '../../lib/format';
import { notifyError, notifyInfo } from '../../lib/notify';
import { AlertRuleDrawer } from './AlertRuleDrawer';

const SNOOZE_OPTIONS = [4, 24, 72, 168];

export function AlertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$org/alerts' });
  const { organisation, can } = useSession();
  const org = organisation!.slug;

  const [ruleId, setRuleId] = useState<number | null>(null);
  const [isRuleOpen, setRuleOpen] = useState(false);

  const tab = search.tab ?? 'events';
  const setTab = (value: string | null) =>
    void navigate({ to: '/$org/alerts', params: { org }, search: { tab: value ?? 'events' } });

  const summary = useQuery({
    queryKey: ['alerts', 'summary'],
    queryFn: async () => unwrap(await api.GET('/api/v1/alerts/events/summary/')),
  });

  return (
    <>
      <PageHeader
        title={t('alerts.title')}
        subtitle={organisation?.name}
        actions={
          can('alerts.manage_rules') && tab === 'rules' ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setRuleId(null);
                setRuleOpen(true);
              }}
            >
              {t('alerts.newRule')}
            </Button>
          ) : null
        }
      />

      {summary.data ? (
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md" mb="md">
          <SummaryTile
            label={t('alerts.CRITICAL')}
            value={summary.data.critical}
            color="red"
          />
          <SummaryTile
            label={t('alerts.WARNING')}
            value={summary.data.warning}
            color="orange"
          />
          <SummaryTile label={t('alerts.INFO')} value={summary.data.info} color="blue" />
          <SummaryTile
            label={t('alerts.unacknowledged', { count: summary.data.unacknowledged })}
            value={summary.data.unacknowledged}
            color="gray"
          />
        </SimpleGrid>
      ) : null}

      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="events">{t('alerts.events')}</Tabs.Tab>
          <Tabs.Tab value="rules">{t('alerts.rules')}</Tabs.Tab>
          <Tabs.Tab value="history">{t('alerts.RESOLVED')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'events' ? <EventList open /> : null}
      {tab === 'history' ? <EventList open={false} /> : null}
      {tab === 'rules' ? (
        <RuleList
          onEdit={(id) => {
            setRuleId(id);
            setRuleOpen(true);
          }}
        />
      ) : null}

      <AlertRuleDrawer
        opened={isRuleOpen}
        onClose={() => setRuleOpen(false)}
        ruleId={ruleId}
      />
    </>
  );
}

function SummaryTile({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card p="md">
      <Text fz="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fz={28} fw={700} c={value > 0 ? color : undefined} lh={1.2}>
        {value}
      </Text>
    </Card>
  );
}

/** Fired alerts. Open ones can be acknowledged, snoozed or resolved from the row. */
function EventList({ open }: { open: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [page, setPage] = useState(1);

  const events = useQuery({
    queryKey: ['alerts', 'events', open, page],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/alerts/events/', {
          params: {
            query: { open, page, page_size: 50, ordering: '-fired_at' },
          },
        }),
      ),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const acknowledge = useMutation({
    mutationFn: async (id: number) =>
      unwrap(
        await api.POST('/api/v1/alerts/events/{id}/acknowledge/', {
          params: { path: { id } },
          body: {},
        }),
      ),
    onSuccess: invalidate,
    onError: notifyError,
  });

  const snooze = useMutation({
    mutationFn: async ({ id, hours }: { id: number; hours: number }) =>
      unwrap(
        await api.POST('/api/v1/alerts/events/{id}/snooze/', {
          params: { path: { id } },
          body: { hours },
        }),
      ),
    onSuccess: invalidate,
    onError: notifyError,
  });

  const resolve = useMutation({
    mutationFn: async (id: number) =>
      unwrap(
        await api.POST('/api/v1/alerts/events/{id}/resolve/', {
          params: { path: { id } },
        }),
      ),
    onSuccess: invalidate,
    onError: notifyError,
  });

  const acknowledgeAll = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST('/api/v1/alerts/events/acknowledge-all/', { body: {} })),
    onSuccess: async (data) => {
      notifyInfo(t('alerts.unacknowledged', { count: data.acknowledged }));
      await invalidate();
    },
    onError: notifyError,
  });

  const columns = useMemo<ColumnDef<AlertEvent, unknown>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: t('alerts.severity'),
        meta: { width: 110 },
        cell: ({ row }) => <StatusBadge kind="severity" value={row.original.severity} size="sm" />,
      },
      {
        accessorKey: 'title',
        header: t('alerts.rule'),
        cell: ({ row }) => (
          <Stack gap={0}>
            <Text fz="sm" fw={600}>
              {row.original.title}
            </Text>
            <Text fz="xs" c="dimmed">
              {row.original.message}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'subject',
        header: t('alerts.subject'),
        meta: { width: 170 },
        cell: ({ row }) => (
          <Text fz="sm" className="identifier">
            {row.original.subject.label}
          </Text>
        ),
      },
      {
        accessorKey: 'remaining',
        header: t('maintenance.remaining'),
        meta: { width: 110, mono: true, align: 'right' },
        cell: ({ row }) => row.original.remaining ?? '',
      },
      {
        accessorKey: 'fired_at',
        header: t('alerts.firedAt'),
        meta: { width: 140 },
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed">
            {formatDateTime(row.original.fired_at)}
          </Text>
        ),
      },
      {
        accessorKey: 'state',
        header: t('alerts.state'),
        meta: { width: 130 },
        cell: ({ row }) => (
          <Group gap={4} wrap="nowrap">
            <StatusBadge kind="alertState" value={row.original.state} size="sm" />
            {row.original.snoozed_until ? (
              <Tooltip label={formatDateTime(row.original.snoozed_until)} withArrow>
                <IconClock size={14} opacity={0.5} />
              </Tooltip>
            ) : null}
          </Group>
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: 110 },
        cell: ({ row }) =>
          can('alerts.acknowledge') && row.original.is_open ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              {row.original.state === 'ACTIVE' ? (
                <Tooltip label={t('action.acknowledge')} withArrow>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => acknowledge.mutate(row.original.id)}
                  >
                    <IconCheck size={15} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('action.snooze')}>
                    <IconClock size={15} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{t('alerts.snoozeFor')}</Menu.Label>
                  {SNOOZE_OPTIONS.map((hours) => (
                    <Menu.Item
                      key={hours}
                      onClick={() => snooze.mutate({ id: row.original.id, hours })}
                    >
                      {t('alerts.snoozeHours', { count: hours })}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
              <Tooltip label={t('action.resolve')} withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="teal"
                  onClick={() => resolve.mutate(row.original.id)}
                >
                  <IconBellOff size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, can],
  );

  return (
    <Stack gap="sm">
      {open && can('alerts.acknowledge') && (events.data?.count ?? 0) > 0 ? (
        <Button
          variant="light"
          size="compact-sm"
          leftSection={<IconCheck size={14} />}
          loading={acknowledgeAll.isPending}
          onClick={() => acknowledgeAll.mutate()}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('action.acknowledgeAll')}
        </Button>
      ) : null}
      <DataTable
        data={events.data?.results ?? []}
        columns={columns}
        isLoading={events.isLoading}
        emptyTitle={t('alerts.noAlerts')}
        minWidth={1000}
        pagination={{
          page,
          pageSize: 50,
          total: events.data?.count ?? 0,
          onPageChange: setPage,
        }}
      />
    </Stack>
  );
}

/** Configured rules, with an on-demand check so a maintainer can see the effect of a change. */
function RuleList({ onEdit }: { onEdit: (id: number) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [page, setPage] = useState(1);

  const rules = useQuery({
    queryKey: ['alerts', 'rules', page],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/alerts/rules/', {
          params: { query: { page, page_size: 50, ordering: 'name' } },
        }),
      ),
  });

  const evaluate = useMutation({
    mutationFn: async (id: number) =>
      unwrap(
        await api.POST('/api/v1/alerts/rules/{id}/evaluate/', {
          params: { path: { id } },
        }),
      ),
    onSuccess: async (data) => {
      notifyInfo(
        data.fired
          ? (data.event?.message ?? t('alerts.ACTIVE'))
          : (data.reading?.label ?? t('maintenance.OK')),
      );
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: notifyError,
  });

  const toggle = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) =>
      unwrap(
        await api.PATCH('/api/v1/alerts/rules/{id}/', {
          params: { path: { id } },
          body: { is_active: isActive },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'rules'] });
    },
    onError: notifyError,
  });

  const columns = useMemo<ColumnDef<AlertRule, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common.name'),
        cell: ({ row }) => (
          <Stack gap={0}>
            <Text fz="sm" fw={600}>
              {row.original.name}
            </Text>
            <Text fz="xs" c="dimmed">
              {row.original.metric_label}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'subject',
        header: t('alerts.subject'),
        meta: { width: 180 },
        cell: ({ row }) => (
          <Text fz="sm" className="identifier">
            {row.original.subject?.label}
          </Text>
        ),
      },
      {
        id: 'reading',
        header: t('alerts.currentReading'),
        meta: { width: 160 },
        cell: ({ row }) => {
          const reading = row.original.current_reading;
          if (!reading) return null;
          return (
            <Stack gap={0}>
              <Text fz="sm" ff="monospace">
                {reading.value ?? '—'}
                {reading.unit ? ` ${reading.unit}` : ''}
              </Text>
              {reading.remaining ? (
                <Text fz="xs" c={reading.is_breached ? 'red' : 'dimmed'}>
                  {t('maintenance.remaining')}: {reading.remaining}
                </Text>
              ) : null}
            </Stack>
          );
        },
      },
      {
        accessorKey: 'threshold',
        header: t('alerts.threshold'),
        meta: { width: 100, mono: true, align: 'right' },
        cell: ({ row }) => row.original.threshold,
      },
      {
        accessorKey: 'lead_times',
        header: t('alerts.leadTimes'),
        meta: { width: 130 },
        cell: ({ row }) => (
          <Group gap={4} wrap="wrap">
            {(row.original.lead_times ?? []).map((value, index) => (
              <Badge key={index} size="xs" variant="outline">
                {value}
              </Badge>
            ))}
          </Group>
        ),
      },
      {
        accessorKey: 'severity',
        header: t('alerts.severity'),
        meta: { width: 110 },
        cell: ({ row }) => <StatusBadge kind="severity" value={row.original.severity} size="sm" />,
      },
      {
        accessorKey: 'open_event_count',
        header: t('alerts.events'),
        meta: { width: 80, align: 'right' },
        cell: ({ row }) =>
          row.original.open_event_count ? (
            <Badge size="sm" color="red">
              {row.original.open_event_count}
            </Badge>
          ) : null,
      },
      {
        accessorKey: 'is_active',
        header: t('common.active'),
        meta: { width: 90 },
        cell: ({ row }) => (
          <Switch
            size="sm"
            checked={row.original.is_active}
            disabled={!can('alerts.manage_rules')}
            onChange={(event) =>
              toggle.mutate({ id: row.original.id, isActive: event.currentTarget.checked })
            }
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: 50 },
        cell: ({ row }) => (
          <Tooltip label={t('alerts.evaluateNow')} withArrow>
            <ActionIcon
              variant="subtle"
              size="sm"
              loading={evaluate.isPending}
              onClick={(event) => {
                event.stopPropagation();
                evaluate.mutate(row.original.id);
              }}
            >
              <IconRefresh size={15} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, can],
  );

  return (
    <DataTable
      data={rules.data?.results ?? []}
      columns={columns}
      isLoading={rules.isLoading}
      onRowClick={can('alerts.manage_rules') ? (row) => onEdit(row.id) : undefined}
      emptyTitle={t('alerts.rules')}
      minWidth={1100}
      pagination={{
        page,
        pageSize: 50,
        total: rules.data?.count ?? 0,
        onPageChange: setPage,
      }}
    />
  );
}
